import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import {
  fetchProjectMemberRoles,
  updateProjectMemberRole,
  type ProjectMemberRoleRow,
} from '../api/projects';
import { SortableTh, StaticTh } from './SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import '../pages/TasksBoard.css';

interface ProjectRolesTabProps {
  projectId: number;
  viewerUserId: number;
  canEdit: boolean;
}

function notesPreview(text: string, maxLen: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

const ProjectRolesTab: React.FC<ProjectRolesTabProps> = ({ projectId, viewerUserId, canEdit }) => {
  const [members, setMembers] = useState<ProjectMemberRoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectMemberRoleRow | null>(null);
  const [roleDraft, setRoleDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const list = await fetchProjectMemberRoles(projectId, viewerUserId);
      setMembers(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load roles.');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, viewerUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = (m: ProjectMemberRoleRow) => {
    setEditing(m);
    setRoleDraft(m.role);
    setNotesDraft(m.notes);
    setSaveError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
    setSaveError('');
  };

  const rolesSort = useTableSortState();
  const sortedMembers = useMemo(
    () =>
      sortRows(members, rolesSort.sortKey, rolesSort.sortDir, (m, key) => {
        switch (key) {
          case 'username':
            return m.username ?? '';
          case 'role':
            return m.role ?? '';
          case 'notes':
            return m.notes ?? '';
          default:
            return '';
        }
      }),
    [members, rolesSort.sortKey, rolesSort.sortDir]
  );

  const submitEdit = async () => {
    if (!editing || !canEdit) return;
    setSaving(true);
    setSaveError('');
    try {
      const updated = await updateProjectMemberRole(projectId, editing.user_id, {
        user_id: viewerUserId,
        role: roleDraft.trim(),
        notes: notesDraft,
      });
      setMembers((prev) => prev.map((row) => (row.user_id === updated.user_id ? updated : row)));
      closeEdit();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
    return <p className="project-detail-panel-placeholder">Sign in to view project roles.</p>;
  }

  return (
    <div className="project-roles-tab">
      {loading && <p className="project-detail-panel-placeholder">Loading…</p>}
      {error && (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && members.length === 0 && (
        <p className="project-detail-panel-placeholder">No collaborators on this project yet.</p>
      )}
      {!loading && !error && members.length > 0 && (
        <div className="collapsible-table-wrapper">
          <table className="all-tasks-table project-roles-table">
            <thead>
              <tr>
                <SortableTh label="Collaborator" columnKey="username" sortKey={rolesSort.sortKey} sortDir={rolesSort.sortDir} onSort={rolesSort.toggleSort} />
                <SortableTh label="Role" columnKey="role" sortKey={rolesSort.sortKey} sortDir={rolesSort.sortDir} onSort={rolesSort.toggleSort} />
                <SortableTh label="Notes" columnKey="notes" sortKey={rolesSort.sortKey} sortDir={rolesSort.sortDir} onSort={rolesSort.toggleSort} />
                <StaticTh className="project-roles-table__actions-col">Actions</StaticTh>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((m) => (
                <tr key={m.user_id}>
                  <td data-label="Collaborator">
                    <span className="project-roles-username">{m.username}</span>
                    {m.is_owner ? (
                      <span className="dashboard-pill project-roles-owner-pill" title="Project owner">
                        Owner
                      </span>
                    ) : null}
                  </td>
                  <td data-label="Role">{m.role.trim() ? m.role : '—'}</td>
                  <td
                    data-label="Notes"
                    className="project-roles-notes-cell"
                    title={m.notes.trim() ? m.notes : undefined}
                  >
                    {m.notes.trim() ? notesPreview(m.notes, 120) : '—'}
                  </td>
                  <td data-label="Actions" className="project-roles-table__actions-cell">
                    {canEdit ? (
                      <button
                        type="button"
                        className="project-checklist-icon-btn"
                        title={`Edit role and notes for ${m.username}`}
                        aria-label={`Edit ${m.username}`}
                        onClick={() => openEdit(m)}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                    ) : (
                      <span className="project-detail-panel-placeholder" style={{ fontStyle: 'normal' }}>
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={editOpen && editing != null}
        onClose={closeEdit}
        title={editing ? `Edit — ${editing.username}` : 'Edit'}
        narrow
      >
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="project-role-field" className="form-label">
              Role
            </label>
            <input
              id="project-role-field"
              type="text"
              className="form-input"
              value={roleDraft}
              onChange={(e) => setRoleDraft(e.target.value)}
              placeholder="e.g. Lead installer, PM"
              disabled={saving}
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-role-notes" className="form-label">
              Notes
            </label>
            <textarea
              id="project-role-notes"
              className="form-input"
              rows={5}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Optional notes about this person on the project"
              disabled={saving}
            />
          </div>
          {saveError ? (
            <p className="create-order-error" role="alert">
              {saveError}
            </p>
          ) : null}
          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="cancel-button" onClick={closeEdit} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="update-button" onClick={() => void submitEdit()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectRolesTab;
